/**
 * Industry Updates Service Layer
 *
 * CRUD operations and AI drafting for industry updates.
 * Used by the IndustryUpdates page in the analyst portal.
 */

import { apiFetch, apiPost, apiDelete, buildQueryString } from './api';
import type { ApiResponse } from './api';
import type {
  IndustryUpdate,
  IndustryCategory,
  IndustrySeverity,
  IndustryUpdateDraft,
  IndustryUpdateSource,
  IndustryUpdateImage,
} from '../data/types';

// ============================================================================
// GET
// ============================================================================

interface GetIndustryUpdatesParams {
  tenantId?: string;
  includeGlobal?: boolean;
  includeInactive?: boolean;
  category?: IndustryCategory;
}

interface IndustryUpdatesResponse {
  updates: IndustryUpdate[];
}

export async function getIndustryUpdates(
  params: GetIndustryUpdatesParams = {}
): Promise<ApiResponse<IndustryUpdatesResponse>> {
  const qs = buildQueryString(params as Record<string, unknown>);
  return apiFetch<IndustryUpdatesResponse>(`/industry-updates${qs}`);
}

// ============================================================================
// CREATE
// ============================================================================

interface CreateIndustryUpdatePayload {
  tenantId?: string | null;
  title: string;
  content: string;
  category: IndustryCategory;
  severity?: IndustrySeverity | null;
  effectiveFrom: string;
  effectiveUntil?: string | null;
  industries?: string[] | null;
  sources?: IndustryUpdateSource[];
  images?: IndustryUpdateImage[];
  searchQuery?: string | null;
  generationModel?: string | null;
}

export async function createIndustryUpdate(
  payload: CreateIndustryUpdatePayload
): Promise<ApiResponse<IndustryUpdate>> {
  return apiPost<IndustryUpdate>('/industry-updates', payload);
}

// ============================================================================
// UPDATE
// ============================================================================

interface UpdateIndustryUpdatePayload {
  tenantId?: string | null;
  title?: string;
  content?: string;
  category?: IndustryCategory;
  severity?: IndustrySeverity | null;
  effectiveFrom?: string;
  effectiveUntil?: string | null;
  isActive?: boolean;
  industries?: string[] | null;
  sources?: IndustryUpdateSource[] | null;
  images?: IndustryUpdateImage[] | null;
  searchQuery?: string | null;
  generationModel?: string | null;
}

export async function updateIndustryUpdate(
  updateId: string,
  payload: UpdateIndustryUpdatePayload
): Promise<ApiResponse<IndustryUpdate>> {
  return apiFetch<IndustryUpdate>(`/industry-updates/${updateId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

// ============================================================================
// DELETE
// ============================================================================

export async function deleteIndustryUpdate(
  updateId: string,
  hard = false
): Promise<ApiResponse<{ message: string }>> {
  const qs = hard ? '?hard=true' : '';
  return apiDelete<{ message: string }>(`/industry-updates/${updateId}${qs}`);
}

// ============================================================================
// AI DRAFT
// ============================================================================

interface DraftIndustryUpdatePayload {
  category: IndustryCategory;
  context?: string;
  searchQuery?: string;
}

export async function draftIndustryUpdate(
  payload: DraftIndustryUpdatePayload
): Promise<ApiResponse<IndustryUpdateDraft>> {
  return apiPost<IndustryUpdateDraft>('/industry-updates/draft', payload);
}

// ============================================================================
// SOURCE VERIFICATION
// ============================================================================

export interface VerifySourceResult {
  ok: boolean;
  status: number;
  finalUrl?: string;
  title?: string | null;
  lastModified?: string | null;
  verifiedAt: string;
  error?: string;
}

export async function verifyIndustryUpdateSource(
  url: string
): Promise<ApiResponse<VerifySourceResult>> {
  return apiPost<VerifySourceResult>('/industry-updates/verify-source', { url });
}

// ============================================================================
// IMAGE UPLOAD
// ============================================================================

export interface ImageUploadTicket {
  uploadUrl: string;
  publicUrl: string;
  blobPath: string;
  contentType: string;
  expiresAt: string;
}

export async function requestIndustryUpdateImageSas(
  filename: string,
  contentType: string
): Promise<ApiResponse<ImageUploadTicket>> {
  return apiPost<ImageUploadTicket>('/industry-updates/images/sas', {
    filename,
    contentType,
  });
}

export interface UploadedImage {
  url: string;        // Read SAS URL (7 days)
  blobPath: string;   // Durable identifier ("container/path/to/blob")
}

/**
 * Uploads a File directly to Azure Blob via a short-lived SAS.
 * Returns both the read SAS URL (good for immediate preview) and the durable
 * blobPath (persisted on the record so the API can re-sign past 7 days).
 */
export async function uploadIndustryUpdateImage(file: File): Promise<UploadedImage> {
  const ticketRes = await requestIndustryUpdateImageSas(file.name, file.type || 'image/png');
  if (!ticketRes.success) {
    throw new Error(ticketRes.error || 'Failed to obtain upload URL');
  }
  const ticket = ticketRes.data;
  const putRes = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'content-type': ticket.contentType,
    },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`);
  }
  return { url: ticket.publicUrl, blobPath: ticket.blobPath };
}

// ============================================================================
// HELPERS
// ============================================================================

const CATEGORY_LABELS: Record<IndustryCategory, string> = {
  patch_alert: 'Patch Alert',
  trend_analysis: 'Trend Analysis',
  global_context: 'Global Context',
  best_practice: 'Best Practice',
};

export function getCategoryLabel(category: IndustryCategory | string): string {
  return CATEGORY_LABELS[category as IndustryCategory] || category;
}

export function getSeverityVariant(
  severity: IndustrySeverity | string | null
): 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'gray' {
  switch (severity) {
    case 'critical': return 'red';
    case 'high': return 'orange';
    case 'medium': return 'yellow';
    case 'low': return 'green';
    case 'informational': return 'blue';
    default: return 'gray';
  }
}
